import { Component, ChangeDetectionStrategy, input, computed } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface IZamanCizelgesiOge {
  baslik: string;
  aciklama?: string;
  zaman?: string;
  renk?: string;
  simge?: string;
  tamamlandi?: boolean;
}

@Component({
  selector: 'widget-zaman-cizelgesi',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @for (oge of ogeler(); track oge.baslik; let i = $index) {
      <div class="zc-oge"
           [class.tamamlandi]="oge.tamamlandi"
           [class.aktif]="aktifIndex() === i"
           [class.gelecek]="!oge.tamamlandi && aktifIndex() !== i"
           [style.animation-delay.ms]="i * 100">
        <div class="zc-sol">
          <div class="zc-dot" [style.--dot-renk]="oge.renk || varsayilanRenk(i)">
            @if (oge.simge) {
              <span class="zc-simge">{{ oge.simge }}</span>
            } @else if (oge.tamamlandi) {
              <span class="zc-ok">&#10003;</span>
            }
          </div>
          @if (i < ogeler().length - 1) {
            <div class="zc-cizgi" [class.dolu]="oge.tamamlandi"></div>
          }
        </div>
        <div class="zc-icerik">
          <div class="zc-baslik-satir">
            <span class="zc-baslik">{{ oge.baslik }}</span>
            @if (oge.zaman) {
              <span class="zc-zaman">{{ oge.zaman }}</span>
            }
          </div>
          @if (oge.aciklama) {
            <p class="zc-aciklama">{{ oge.aciklama }}</p>
          }
        </div>
      </div>
    }
  `,
  styles: [`
    :host { display: block; padding: 4px 0; }

    .zc-oge {
      display: flex; gap: 10px; opacity: 0;
      animation: zcFadeIn 0.35s ease forwards;
    }

    .zc-sol {
      display: flex; flex-direction: column; align-items: center;
      flex-shrink: 0; width: 24px;
    }

    .zc-dot {
      width: 24px; height: 24px; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      font-size: 12px; flex-shrink: 0; position: relative;
      border: 2px solid var(--dot-renk, #1890ff);
      background: transparent;
      color: var(--dot-renk, #1890ff);
      transition: all 0.25s ease;
    }

    .tamamlandi .zc-dot {
      background: var(--dot-renk, #1890ff);
      color: #fff;
    }

    .aktif .zc-dot { animation: zcNabiz 1.8s ease-in-out infinite; }

    .gelecek .zc-dot {
      border-color: var(--py-hover, rgba(128,128,128,0.25));
      color: var(--py-hover, rgba(128,128,128,0.25));
    }

    .zc-ok { font-size: 11px; font-weight: 700; line-height: 1; }
    .zc-simge { font-size: 13px; line-height: 1; }

    .zc-cizgi {
      width: 2px; flex: 1; min-height: 16px;
      background: var(--py-hover, rgba(128,128,128,0.18));
      margin: 3px 0;
      transition: background 0.25s ease;
    }
    .zc-cizgi.dolu { background: var(--dot-renk, #1890ff); opacity: 0.5; }

    .zc-icerik {
      flex: 1; min-width: 0;
      padding-bottom: 14px;
    }

    .zc-baslik-satir {
      display: flex; align-items: baseline; gap: 6px;
      justify-content: space-between;
    }

    .zc-baslik {
      font-size: 13px; font-weight: 600; line-height: 24px;
      color: var(--py-metin, #262626);
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }

    .gelecek .zc-baslik { opacity: 0.45; }

    .zc-zaman {
      font-size: 11px; font-weight: 400; white-space: nowrap; flex-shrink: 0;
      color: var(--py-metin, #262626); opacity: 0.45;
    }

    .zc-aciklama {
      margin: 2px 0 0; font-size: 12px; line-height: 1.45;
      color: var(--py-metin, #262626); opacity: 0.6;
    }

    .gelecek .zc-aciklama { opacity: 0.3; }

    @keyframes zcFadeIn {
      from { opacity: 0; transform: translateY(8px); }
      to   { opacity: 1; transform: translateY(0); }
    }

    @keyframes zcNabiz {
      0%, 100% { box-shadow: 0 0 0 0 var(--dot-renk, rgba(24,144,255,0.5)); }
      50%      { box-shadow: 0 0 0 6px transparent; }
    }
  `],
})
export class ZamanCizelgesiComponent {
  ogeler = input<IZamanCizelgesiOge[]>([]);

  aktifIndex = computed(() => {
    const idx = this.ogeler().findIndex(o => !o.tamamlandi);
    return idx === -1 ? -1 : idx;
  });

  varsayilanRenk(index: number): string {
    const renkler = ['#1890ff', '#52c41a', '#faad14', '#722ed1', '#13c2c2', '#eb2f96', '#fa8c16', '#f5222d'];
    return renkler[index % renkler.length];
  }
}
